import {
	IkeCloneable,
	IkeCollectorType,
	IkeFieldType,
	type SubFormEquipmentTypeState,
	SubFormType,
} from '@ikegps/core-api';
import { IkeId } from '../../../../id';
import { FormModelField } from '../../form.field';
import { FormModelFieldNestedList } from '../../nested.list/form.nested.list';
import {
	isValidDefaultEquipmentTypeSelectList,
	isValidEquipmentTypeField,
	isValidOrientationField,
	isValidEquipmentHeightField,
} from './form.subform.equipment';
import { FormModelFieldSelectList } from '../../form.select.list';
import { IkePoleForemanFieldIds, PoleFieldIds } from '../../../pole/integrations';
import { FormModelState } from '../../form.state';
import { type FormFieldSubFormState, FormModelFieldSubForm } from '../form.subform';
import { LOCKED_MODEL_POLE_FIELDS } from '../../form.field.lock';
import {
	addOwnerField,
	isValidDefaultOwnerField,
	isValidOwnerField,
} from '../../nested.list/form.nested.list.owner.util';

export class EquipmentSubFormMutator {
	private field: FormModelFieldSubForm;
	constructor(field: FormModelFieldSubForm) {
		this.field = field;
	}
	private get state(): FormModelState {
		return this.field.getForm().state;
	}

	public enable() {
		let ownerField =
			this.field.fields.find(
				(field) =>
					(field.id === PoleFieldIds.EQUIPMENT.OWNER || field.id === IkePoleForemanFieldIds.EQUIPMENT_OWNER) &&
					isValidOwnerField(field)
			) ??
			this.field.fields.find((field) => isValidDefaultOwnerField(field)) ??
			this.field.fields.find((field) => isValidOwnerField(field));
		let typeField =
			this.field.fields.find(
				(field) =>
					(field.id !== ownerField?.id && field.id === PoleFieldIds.EQUIPMENT.TYPE) ||
					field.id === IkePoleForemanFieldIds.EQUIPMENT_TYPE
			) ?? this.field.fields.find((field) => field.id !== ownerField?.id && isValidEquipmentTypeField(field));
		let orientationField =
			this.field.fields.find(
				(field) =>
					field.id === PoleFieldIds.EQUIPMENT.ORIENTATION || field.id === IkePoleForemanFieldIds.EQUIPMENT_ORIENTATION
			) ?? this.field.fields.find(isValidOrientationField);
		let attachmentHeightField =
			this.field.fields.find(
				(field) =>
					field.id === PoleFieldIds.EQUIPMENT.ATTACHMENT_HEIGHT ||
					field.id === IkePoleForemanFieldIds.EQUIPMENT_ATTACHMENT_HEIGHT
			) ?? this.field.fields.find((field) => isValidEquipmentHeightField(field, 'attachmentHeightFieldId'));

		if (!typeField) {
			typeField = this.addEquipmentTypeField();
		}
		if (!orientationField) {
			orientationField = this.addOrientationField();
		}
		if (!ownerField) {
			ownerField = this.addOwnerField();
		}
		if (!attachmentHeightField) {
			attachmentHeightField = this.addAttachmentHeightField();
		}

		if (
			(typeField.id === PoleFieldIds.EQUIPMENT.TYPE || typeField.id === IkePoleForemanFieldIds.EQUIPMENT_TYPE) &&
			FormModelFieldSelectList.isSelectList(typeField) &&
			isValidDefaultEquipmentTypeSelectList(typeField)
		) {
			const selectOptions = typeField.options.filter((opt) => opt.value !== FormModelFieldSelectList.UNSELECTED_VALUE);
			this.state.setFieldValue(this.field.id + ':' + typeField.id, {
				type: IkeFieldType.NESTED_LIST,
				id: typeField.id,
				displayName: typeField.displayName,
				isGenerated: true,
			});
			delete this.field['_fields'][typeField.id];
			const nestedTypeField = this.field.fields.find((field) => field.id === typeField.id);
			if (nestedTypeField != null && FormModelFieldNestedList.isNestedList(nestedTypeField)) {
				const builder = nestedTypeField.Mutator.builder;
				const typeGroup = builder.createGroup(nestedTypeField.displayName);
				for (const option of selectOptions) {
					typeGroup.createValue(option.text);
				}
				nestedTypeField.Mutator.init(builder.options);
			}
		}

		if (
			orientationField.type === IkeFieldType.NUMBER &&
			(orientationField.id === PoleFieldIds.EQUIPMENT.ORIENTATION ||
				orientationField.id === IkePoleForemanFieldIds.EQUIPMENT_ORIENTATION)
		) {
			this.state.setFieldValue(this.field.id + ':' + orientationField.id, {
				...orientationField.stateValue,
				type: IkeFieldType.ORIENTATION,
				locked: true,
				isGenerated: true, // so we know it has been converted
			});
		}

		const bottomHeightField =
			this.field.fields.find((field) => field.id === PoleFieldIds.EQUIPMENT.ATTACHMENT_BOTTOM_HEIGHT) ??
			this.field.fields.find(
				(field) => isValidEquipmentHeightField(field, 'bottomHeightFieldId') && field.id !== attachmentHeightField.id
			);

		this.setFieldReferences(
			typeField.id,
			orientationField.id,
			ownerField.id,
			attachmentHeightField.id,
			bottomHeightField?.id
		);
		this.updateLockState(true);
	}

	public disable() {
		const equipmentState = this.field.subFormEquipmentState;

		if (equipmentState?.orientationFieldId) {
			const orientationField = this.field.fields.find((f) => f.id === equipmentState.orientationFieldId);
			if (orientationField && this.wasOrientationConvertedFromNumber(orientationField)) {
				this.revertOrientationFieldToNumber(orientationField);
			}
		}

		this.updateLockState(false);
		this.state.setFieldValue(this.field.fieldId, {
			...this.field.stateValue,
			subFormType: SubFormType.NONE,
			equipmentTypeState: undefined, // Clear to avoid stale field references
			...(!LOCKED_MODEL_POLE_FIELDS[this.field.id] && { locked: false }),
		} satisfies FormFieldSubFormState);
	}

	private wasOrientationConvertedFromNumber(field: FormModelField): boolean {
		return (
			field.type === IkeFieldType.ORIENTATION &&
			(field.id === PoleFieldIds.EQUIPMENT.ORIENTATION || field.id === IkePoleForemanFieldIds.EQUIPMENT_ORIENTATION) &&
			field.isGenerated
		);
	}

	private revertOrientationFieldToNumber(field: FormModelField) {
		this.state.setFieldValue(this.field.id + ':' + field.id, {
			...field.stateValue,
			type: IkeFieldType.NUMBER,
			isGenerated: false,
		});
		this.updateLockState(true);
	}

	public setFieldReferences(
		typeFieldId: string,
		orientationFieldId: string,
		ownerFieldId: string,
		attachmentHeightFieldId: string,
		bottomHeightFieldId?: string
	) {
		if (!this.field.hasField(typeFieldId)) {
			throw new Error(`Equipment Type field '${typeFieldId}' not found`);
		}
		if (!this.field.hasField(orientationFieldId)) {
			throw new Error(`Equipment Orientation field '${orientationFieldId}' not found`);
		}
		if (!this.field.hasField(ownerFieldId)) {
			throw new Error(`Equipment Owner field '${ownerFieldId}' not found`);
		}
		if (!this.field.hasField(attachmentHeightFieldId)) {
			throw new Error(`Equipment Attachment Height field '${attachmentHeightFieldId}' not found`);
		}
		if (bottomHeightFieldId && !this.field.hasField(bottomHeightFieldId)) {
			throw new Error(`Equipment Bottom Height field '${bottomHeightFieldId}' not found`);
		}
		this.state.setFieldValue(this.field.fieldId, {
			...this.field.stateValue,
			subFormType: SubFormType.EQUIPMENT,
			equipmentTypeState: {
				typeFieldId,
				orientationFieldId,
				ownerFieldId,
				attachmentHeightFieldId,
				bottomHeightFieldId,
			},
			locked: true,
		} satisfies FormFieldSubFormState);
		this.setFieldsMetadata(typeFieldId, ownerFieldId, attachmentHeightFieldId, bottomHeightFieldId);
		this.updateLockState(true);
	}

	public setFieldsMetadata(
		typeFieldId: string,
		ownerFieldId: string,
		attachmentHeightFieldId: string,
		bottomHeightFieldId?: string
	) {
		if (typeFieldId === PoleFieldIds.EQUIPMENT.TYPE || typeFieldId === IkePoleForemanFieldIds.EQUIPMENT_TYPE) {
			this.field.getField(typeFieldId).Mutator.title(true);
		}
		if (ownerFieldId === PoleFieldIds.EQUIPMENT.OWNER || ownerFieldId === IkePoleForemanFieldIds.EQUIPMENT_OWNER) {
			this.field.getField(ownerFieldId).Mutator.cloneable(IkeCloneable.ALL);
		}
		if (
			attachmentHeightFieldId === PoleFieldIds.EQUIPMENT.ATTACHMENT_HEIGHT ||
			attachmentHeightFieldId === IkePoleForemanFieldIds.EQUIPMENT_ATTACHMENT_HEIGHT
		) {
			this.field.getField(attachmentHeightFieldId).Mutator.collector(IkeCollectorType.OFFICE);
		}
		if (bottomHeightFieldId && bottomHeightFieldId === PoleFieldIds.EQUIPMENT.ATTACHMENT_BOTTOM_HEIGHT) {
			this.field.getField(bottomHeightFieldId).Mutator.collector(IkeCollectorType.OFFICE);
		}
	}

	public selectEquipmentTypeField(fieldId: string) {
		this.updateFieldReference('typeFieldId', fieldId);
	}

	public selectOrientationField(fieldId: string) {
		this.updateFieldReference('orientationFieldId', fieldId);
	}

	public selectOwnerField(fieldId: string) {
		this.updateFieldReference('ownerFieldId', fieldId);
	}

	public selectAttachmentHeightField(fieldId: string) {
		this.updateFieldReference('attachmentHeightFieldId', fieldId);
	}

	public selectBottomHeightField(fieldId: string | null) {
		this.updateFieldReference('bottomHeightFieldId', fieldId);
	}

	private updateFieldReference(key: keyof SubFormEquipmentTypeState, newFieldId: string | null) {
		const currentState = this.field.stateValue.equipmentTypeState;
		if (!currentState) {
			return;
		}
		const oldFieldId = currentState[key];
		const oldField = oldFieldId ? this.field.fields.find((f) => f.id === oldFieldId) : undefined;
		if (
			key === 'orientationFieldId' &&
			oldField &&
			oldFieldId !== newFieldId &&
			this.wasOrientationConvertedFromNumber(oldField)
		) {
			this.revertOrientationFieldToNumber(oldField);
		}
		const newState: SubFormEquipmentTypeState = {
			...currentState,
			[key]: newFieldId,
		};
		this.state.setFieldValue(this.field.fieldId, {
			...this.field.stateValue,
			subFormType: SubFormType.EQUIPMENT,
			equipmentTypeState: newState,
			locked: true,
		} satisfies FormFieldSubFormState);
		const newField = this.field.fields.find((f) => f.id === newFieldId);
		if (oldField && !LOCKED_MODEL_POLE_FIELDS[oldField.id]) {
			oldField.Mutator.setLockState(false);
		}
		if (key !== 'bottomHeightFieldId' && key !== 'ownerFieldId' && key !== 'typeFieldId') {
			newField?.Mutator.setLockState(true);
		}
	}
	public addEquipmentTypeField(index: number = 0) {
		const equipmentTypeField = this.field.createField<FormModelFieldNestedList>(
			IkeFieldType.NESTED_LIST,
			{
				id: IkeId.generate(),
				displayName: 'Type',
				title: true,
				locked: false,
				isGenerated: true,
			},
			index
		);

		const builder = equipmentTypeField.Mutator.builder;
		const typeGroup = builder.createGroup('Type');
		typeGroup.createValue('Capacitor Bank');
		typeGroup.createValue('Crossarm');
		typeGroup.createValue('Recloser');
		typeGroup.createValue('Regulator');
		typeGroup.createValue('Riser');
		typeGroup.createValue('Streetlight');
		typeGroup.createValue('Transformer - Single Phase');
		typeGroup.createValue('Transformer - Three Phase');
		typeGroup.createValue('Switch');

		equipmentTypeField.Mutator.init(builder.options);
		equipmentTypeField.Mutator.title(true);

		return equipmentTypeField;
	}
	private addOrientationField() {
		return this.field.createField(IkeFieldType.ORIENTATION, {
			id: IkeId.generate(),
			displayName: 'Orientation',
			locked: true,
			isGenerated: true,
		});
	}

	public addOwnerField(index: number = 2) {
		return addOwnerField(this.field, index);
	}

	public addAttachmentHeightField() {
		const attachmentHeightField = this.field.createField(IkeFieldType.HEIGHT, {
			id: IkeId.generate(),
			displayName: 'Attachment Height',
			locked: true,
			isGenerated: true,
		});
		attachmentHeightField.Mutator.collector(IkeCollectorType.OFFICE);
		return attachmentHeightField;
	}
	public addBottomHeightField(index: number = 4) {
		const bottomHeightField = this.field.createField(
			IkeFieldType.HEIGHT,
			{
				id: IkeId.generate(),
				displayName: 'Bottom Height',
				locked: false,
				isGenerated: true,
			},
			index
		);
		bottomHeightField.Mutator.collector(IkeCollectorType.OFFICE);
		return bottomHeightField;
	}
	private updateLockState(isLocked: boolean) {
		const typeField = this.field.fields.find((field) => field.id === this.field.subFormEquipmentState?.typeFieldId);
		const orientationField = this.field.fields.find(
			(field) => field.id === this.field.subFormEquipmentState?.orientationFieldId
		);
		const ownerField = this.field.fields.find((field) => field.id === this.field.subFormEquipmentState?.ownerFieldId);
		const attachmentHeightField = this.field.fields.find(
			(field) => field.id === this.field.subFormEquipmentState?.attachmentHeightFieldId
		);
		const bottomHeightField = this.field.fields.find(
			(field) => field.id === this.field.subFormEquipmentState?.bottomHeightFieldId
		);
		if (isLocked) {
			orientationField?.Mutator.setLockState(isLocked);
			attachmentHeightField?.Mutator.setLockState(isLocked);
			return;
		}
		if (typeField && (!LOCKED_MODEL_POLE_FIELDS[typeField.id] || typeField?.isGenerated)) {
			typeField.Mutator.setLockState(isLocked);
		}
		if (orientationField && (!LOCKED_MODEL_POLE_FIELDS[orientationField.id] || orientationField.isGenerated)) {
			orientationField.Mutator.setLockState(isLocked);
		}
		if (ownerField && (!LOCKED_MODEL_POLE_FIELDS[ownerField.id] || ownerField.isGenerated)) {
			ownerField.Mutator.setLockState(isLocked);
		}
		if (
			attachmentHeightField &&
			(!LOCKED_MODEL_POLE_FIELDS[attachmentHeightField.id] || attachmentHeightField.isGenerated)
		) {
			attachmentHeightField.Mutator.setLockState(isLocked);
		}
		if (bottomHeightField && (!LOCKED_MODEL_POLE_FIELDS[bottomHeightField.id] || bottomHeightField.isGenerated)) {
			bottomHeightField.Mutator.setLockState(isLocked);
		}
	}
}
